import React, {
  forwardRef,
  useEffect,
  useState,
  MouseEventHandler,
  KeyboardEventHandler,
} from 'react';
import { TextField, TextFieldProps, ChangeOptions } from '../main';
import noop from 'lodash/noop';
import { dates } from '@y2/packages-utils/dates';
import { log } from '@y2/log';

type ChangeMethod = TextFieldProps['onChange'];

const toString = (date?: Date) =>
  date ? dates.format(date, 'yyyy-MM-dd') : '';

type Props = Omit<TextFieldProps, 'value' | 'onChange'> & {
  value: Date | undefined;
  onChange: (value: Date | undefined, options: ChangeOptions) => void;
  format?: string;
};

export const DateTextField = forwardRef<HTMLInputElement, Props>(
  (
    {
      type,
      children,
      value,
      autoComplete = 'off',
      onChange = noop,
      ...fieldProps
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState(toString(value));

    useEffect(() => {
      setInputValue(value ? toString(value) : '');
    }, [value]);

    const handleChange: ChangeMethod = (inputValue, options) => {
      const parsedDate = dates.parseISO(inputValue);
      setInputValue(inputValue ? toString(parsedDate) : '');
      onChange(parsedDate, options);
    };

    const handleOnClickInput: MouseEventHandler<HTMLInputElement> = (event) => {
      try {
        event.currentTarget.showPicker();
      } catch (error) {
        log.info('showPicker is not available');
      }
    };

    const handleOnKeyDownInput: KeyboardEventHandler<HTMLInputElement> = (
      event,
    ) => event.preventDefault();

    return (
      <TextField
        {...fieldProps}
        ref={ref}
        value={inputValue}
        onChange={handleChange}
        type={'date'}
        autoComplete={autoComplete}
        onKeyDown={handleOnKeyDownInput}
        onClick={handleOnClickInput}
      >
        {children}
      </TextField>
    );
  },
);
